import { useCallback, useState } from "react";
import { Upload, X, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { validateImageFile, ACCEPTED_IMAGE_MIME } from "@/lib/imageCompression";

interface ImageDropZoneProps {
  currentUrl?: string | null;
  onUpload: (file: File) => Promise<void>;
  accept?: string;
  label?: string;
  className?: string;
  height?: string;
  maxSizeMB?: number;
}

const ImageDropZone = ({ currentUrl, onUpload, accept = ACCEPTED_IMAGE_MIME, label = "Glisser ou cliquer pour uploader (JPG, PNG, WebP)", className, height = "h-40", maxSizeMB = 20 }: ImageDropZoneProps) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = useCallback(async (file: File) => {
    const v = validateImageFile(file, { maxOriginalMB: maxSizeMB });
    if (!v.ok) {
      toast.error(v.error!);
      return;
    }
    setUploading(true);
    try {
      await onUpload(file);
    } catch (err: any) {
      console.error("[ImageDropZone] upload failed:", err);
      const msg = err?.message || "erreur inconnue";
      const friendly = /format|size|too large|payload|mime|heic/i.test(msg)
        ? "Format non supporté ou image trop lourde. Réessayez avec un JPG, PNG ou WebP."
        : "Échec de l'upload : " + msg;
      toast.error(friendly);
    } finally {
      setUploading(false);
    }
  }, [onUpload, maxSizeMB]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div
      className={cn(
        "relative rounded-lg border-2 border-dashed transition-all duration-200 overflow-hidden",
        dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/40",
        className
      )}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      {currentUrl ? (
        <img src={currentUrl} alt="" className={cn("w-full object-contain bg-secondary/20", height)} />
      ) : (
        <div className={cn("flex flex-col items-center justify-center gap-2 text-muted-foreground", height)}>
          <Upload className="w-8 h-8" />
          <span className="font-body text-xs text-center px-2">{label}</span>
        </div>
      )}

      {uploading && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      <label className="absolute inset-0 cursor-pointer">
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            // Reset so the same file can be re-selected after an error
            e.target.value = "";
          }}
          disabled={uploading}
        />
      </label>
    </div>
  );
};

export default ImageDropZone;
